import { useEffect, useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import { fetchStats } from "@/lib/api";

const TESTIMONIALS = [
  {
    quote:
      "XcellerEYES has changed how we manage persistent epithelial defects. The handling is simple, and we are seeing meaningful reepithelialization.",
    author: "Cornea Specialist",
    role: "Academic Practice, Northeast",
  },
  {
    quote:
      "Ambient storage removed a real operational pain point. We can stock the product without restructuring our clinic logistics.",
    author: "Practice Administrator",
    role: "Multi-Site Group, Southeast",
  },
  {
    quote:
      "For surgical temporary ocular discomfort, this has become a first-line tool in our refractive workflow.",
    author: "Refractive Surgeon",
    role: "Private Practice, West",
  },
];

export default function ClinicalEvidence() {
  const [stats, setStats] = useState([]);
  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  return (
    <section data-testid="clinical-evidence" className="bg-slate1 text-white py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-15" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-radial-glow blur-3xl opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">Clinical Evidence</div>
              <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl leading-tight font-medium">
                Outcomes that <span className="text-gradient-teal-bright">earn trust</span>.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/65 text-lg leading-relaxed">
                Lyophilized amniotic membrane has been studied across multiple ocular indications.
                Below are representative outcomes and operational metrics from the field.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 mb-20">
          {stats.map((s, i) => (
            <ScrollReveal key={s.label} delay={i * 100}>
              <div className="border-l border-teal/30 pl-5 md:pl-7">
                <div className="font-display font-medium text-5xl md:text-6xl shimmer-text leading-none">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-4 text-xs md:text-sm text-white/60 tracking-wide max-w-[160px]">{s.label}</div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <ScrollReveal key={i} delay={i * 100}>
              <figure
                data-testid={`testimonial-${i}`}
                className="glass-dark rounded-3xl p-8 h-full flex flex-col hover:border-teal/40 transition-colors"
              >
                <svg className="h-6 w-6 text-teal mb-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 7H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2v2a4 4 0 0 1-4 4v2a6 6 0 0 0 6-6V9a2 2 0 0 0 0-2zm12 0h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2v2a4 4 0 0 1-4 4v2a6 6 0 0 0 6-6V9a2 2 0 0 0 0-2z" />
                </svg>
                <blockquote className="text-lg leading-relaxed text-white/85 flex-1">{t.quote}</blockquote>
                <figcaption className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-semibold text-white">{t.author}</div>
                  <div className="text-sm text-white/55">{t.role}</div>
                </figcaption>
              </figure>
            </ScrollReveal>
          ))}
        </div>

        <p className="mt-12 text-xs text-white/40 max-w-3xl">
          * Representative practice-reported outcomes. Individual results vary. Refer to peer-reviewed literature and the
          XcellerEYES instructions for use for indication-specific evidence.
        </p>
      </div>
    </section>
  );
}
